import React, { useState } from 'react';

import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import Tooltip from '@material-ui/core/Tooltip';

import { GetHelp, InfoButton } from '../FormWidgets/Description';
import ReplayIcon from '@material-ui/icons/Replay';
import { MySlider } from '../FormWidgets/MySlider';
import { GetMinBorder, GetMaxBorder, GetUnit } from '../Constants/constants';
import { makeStyles } from '@material-ui/core/styles';

import { useTranslation } from 'react-i18next';

import Box from '@material-ui/core/Box';
import MuiSwitch from './SettingsSwitch';

import { ValueBox } from '../FormWidgets/SettingGroup';
import {
  controller_project_exclude_features as cpef,
  controller_type_exclude_features as ctef
} from '../Constants/ControllerFeatures';

const useStyles = makeStyles((theme) => ({
  settingTooltip: {
    backgroundColor: theme.palette.grey[700],
    fontSize: '0.95rem',
    fontWeight: 700,
    lineHeight: 1.25,
    maxWidth: 'none',
    padding: '6px 10px'
  },
  settingTooltipArrow: {
    color: theme.palette.grey[700]
  }
}));

export function ShowSetting({
  id,
  unit_key,
  show,
  cur_value,
  controller_value,
  convert_to_min = false,
  convert_to_hhmmss = false,
  changed,
  controller,
  disabled,
  min,
  max,
  unit,
  off_at,
  type,
  controls,
  num_digits = 1,
  mod_digits = 1,
  mod_steps = undefined,
  wrap_time_over_midnight = false,
  composite = false,
  children,
  valueBoxStyle,
  tooltipTitle
}) {
  const { t } = useTranslation();
  const classes = useStyles();
  const help = GetHelp(id, show, controller.type);
  const displayName = t(show !== undefined ? show : id);

  // State to track which handle was last moved in double sliders
  const [lastMovedHandle, setLastMovedHandle] = useState('min');

  // Check if ValueBox is excluded for this controller-type
  if (
    controller !== undefined &&
    controller.type in ctef &&
    ctef[controller.type]['values'].includes(
      id.indexOf(':') > -1 ? (composite ? id.split(':').slice(-1)[0] : id.split(':')[1]) : id
    )
  )
    return null;
  if (
    controller !== undefined &&
    controller.project_id in cpef &&
    cpef[controller.project_id]['values'].includes(
      id.indexOf(':') > -1 ? (composite ? id.split(':').slice(-1)[0] : id.split(':')[1]) : id
    )
  )
    return null;

  // Update min and max from defaults if not defined
  min = GetMinBorder(min, id) / mod_digits;
  max = GetMaxBorder(max, id) / mod_digits;

  let mode = 'single';
  let cur_value_min;
  let cur_value_max;
  let controller_value_min;
  let controller_value_max;
  let id_max;
  let id_min;
  if (Array.isArray(cur_value)) {
    // double handle
    mode = 'double';
    cur_value_min = cur_value[0];
    cur_value_max = cur_value[1];
    controller_value_min = controller_value[0];
    controller_value_max = controller_value[1];

    id_max = id;
    if (id.includes('upper')) {
      id_min = id.replace('upper', 'lower');
    } else {
      id_min = id.replace('max', 'min');
    }
  }

  const adjusted_value =
    type === 'float'
      ? Number.parseFloat(cur_value) / 10 ** num_digits
      : type === 'negative'
        ? cur_value - 127
        : cur_value;

  const hasChanged =
    changed == null
      ? mode === 'single'
        ? !(cur_value === controller_value)
        : cur_value_min !== controller_value_min || cur_value_max !== controller_value_max
      : changed;
  const convert = (v) =>
    type === 'float' ? v * 10 ** num_digits : type === 'negative' ? v + 127 : v;

  const stepSize = mod_steps ? (max - min) / mod_steps : 1;

  const content = (
    <Box style={{ whiteSpace: 'normal' }}>
      <Box
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          columnGap: '0.75rem'
        }}
      >
        <Box
          alignItems={'center'}
          title={displayName}
          style={{
            alignItems: 'center',
            display: 'flex',
            flex: '1 1 auto',
            minWidth: 0
          }}
        >
          <Typography
            gutterBottom
            style={{
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {displayName}
          </Typography>
          <Box style={{ flex: '0 0 auto' }}>
            <InfoButton content={help.content} img={help.img} />
          </Box>
        </Box>
        <Box
          justify="flex-end"
          style={{
            flex: '0 1 auto',
            marginLeft: 'auto',
            maxWidth: '100%',
            minWidth: 0,
            textAlign: 'right'
          }}
        >
          <Typography gutterBottom style={{ textAlign: 'right', whiteSpace: 'normal' }}>
            {mode === 'single' ? (
              <ShowCurValue
                name={id}
                value={cur_value}
                convert_to_min={convert_to_min}
                convert_to_hhmmss={convert_to_hhmmss}
                wrap_time_over_midnight={wrap_time_over_midnight}
                unit={unit}
                type={type}
                num_digits={num_digits}
                off_at={off_at}
              />
            ) : (
              <>
                <ShowCurValue
                  name={id_min}
                  value={cur_value_min}
                  convert_to_min={convert_to_min}
                  convert_to_hhmmss={convert_to_hhmmss}
                  wrap_time_over_midnight={wrap_time_over_midnight}
                  unit={unit}
                  type={type}
                  num_digits={num_digits}
                  off_at={off_at}
                />
                /&nbsp;
                <ShowCurValue
                  name={id_max}
                  value={cur_value_max}
                  convert_to_min={convert_to_min}
                  convert_to_hhmmss={convert_to_hhmmss}
                  wrap_time_over_midnight={wrap_time_over_midnight}
                  unit={unit}
                  type={type}
                  num_digits={num_digits}
                  off_at={off_at}
                />
              </>
            )}
            <IconButton
              onClick={() => {
                if (mode === 'single') {
                  controls.resetSetting(id);
                } else {
                  if (
                    id_min.includes('vwc_auto_sp_max_allowed_lower') &&
                    id_max.includes('vwc_auto_sp_max_allowed_upper')
                  ) {
                    // Minimal change: reset both ends individually
                    controls.resetSetting(id_min);
                    controls.resetSetting(id_max);
                  } else {
                    controls.resetMinMaxSlider(id_min, id_max);
                  }
                }
              }}
            >
              <ReplayIcon fontSize="small" />
            </IconButton>

            {children}
          </Typography>
        </Box>
      </Box>
      <MySlider
        id={id}
        value={mode === 'single' ? cur_value : [cur_value_min, cur_value_max]}
        convert_to_min={convert_to_min}
        convert_to_hhmmss={convert_to_hhmmss}
        wrap_time_over_midnight={wrap_time_over_midnight}
        name={id}
        unit_key={unit_key ?? id}
        disabled={disabled}
        DecClick={() => {
          if (mode === 'single') {
            if (adjusted_value > min) {
              controls.setValue(id, cur_value - stepSize);
              controls.onChangeCommitted?.(id, cur_value - stepSize);
            }
          } else {
            // For double sliders, adjust the last moved handle
            if (lastMovedHandle === 'max') {
              // Check if max value can be decremented (must stay above min value and above min boundary)
              const effectiveMin = Math.max(min, cur_value_min);
              if (cur_value_max > effectiveMin) {
                controls.setValue(id_max, cur_value_max - 1);
              }
            } else {
              // Check if min value can be decremented (must stay above min boundary)
              // Handle "off" values by checking against off_at threshold
              const effectiveMin = off_at !== undefined ? off_at : min;
              if (cur_value_min > effectiveMin) {
                controls.setValue(id_min, cur_value_min - 1);
              }
            }
          }
        }}
        IncClick={() => {
          if (mode === 'single') {
            if (adjusted_value < max) {
              controls.setValue(id, cur_value + stepSize);
              controls.onChangeCommitted?.(id, cur_value + stepSize);
            }
          } else if (mode === 'phase') {
            if (adjusted_value < max) {
              controls.setValue(id, cur_value + 1);
            }
          } else {
            // mode == 'double'
            if (lastMovedHandle === 'max') {
              // Check if max value can be incremented (must stay below max boundary)
              if (cur_value_max < max) {
                controls.setValue(id_max, cur_value_max + 1);
              }
            } else {
              // Check if min value can be incremented (must stay below max value and below max boundary)
              const effectiveMax = Math.min(max, cur_value_max);
              if (cur_value_min < effectiveMax) {
                controls.setValue(id_min, cur_value_min + 1);
              }
            }
          }
        }}
        onChange={(event, value) => {
          if (mode === 'single') {
            controls.setValue(id, convert(value), false);
          } else {
            // Track which handle was moved by comparing with current values
            if (value[0] !== cur_value_min) {
              setLastMovedHandle('min');
            } else if (value[1] !== cur_value_max) {
              setLastMovedHandle('max');
            }
            if (
              id_min.includes('vwc_auto_sp_max_allowed_lower') &&
              id_max.includes('vwc_auto_sp_max_allowed_upper')
            ) {
              // Minimal change: update both ends individually
              controls.setValue(id_min, convert(value[0]), false);
              controls.setValue(id_max, convert(value[1]), false);
            } else {
              controls.setValueMinMaxSlider(
                id_min,
                id_max,
                convert(value[0]),
                convert(value[1]),
                false
              );
            }
          }
        }}
        // slider released
        onChangeCommitted={(event, value) => {
          if (mode === 'single') {
            controls.setValue(id, convert(value));
            if (controls.onChangeCommitted) {
              controls.onChangeCommitted(id, convert(value));
            }
          } else {
            // Track which handle was moved by comparing with current values
            if (value[0] !== cur_value_min) {
              setLastMovedHandle('min');
            } else if (value[1] !== cur_value_max) {
              setLastMovedHandle('max');
            }
            if (
              id_min.includes('vwc_auto_sp_max_allowed_lower') &&
              id_max.includes('vwc_auto_sp_max_allowed_upper')
            ) {
              // Minimal change: commit both separately
              controls.setValue(id_min, convert(value[0]));
              controls.setValue(id_max, convert(value[1]));
            } else {
              controls.setValueMinMaxSlider(id_min, id_max, convert(value[0]), convert(value[1]));
            }
          }
        }}
        min={min}
        max={max}
        unit={unit}
        type={type}
        num_digits={num_digits}
        mod_digits={mod_digits}
        mod_steps={mod_steps}
        color={hasChanged ? 'secondary' : 'primary'}
      />
    </Box>
  );

  return (
    <React.Fragment>
      <ValueBox style={valueBoxStyle}>
        {tooltipTitle ? (
          <Tooltip
            arrow
            classes={{ tooltip: classes.settingTooltip, arrow: classes.settingTooltipArrow }}
            enterDelay={250}
            placement="top"
            title={tooltipTitle}
          >
            <Box>{content}</Box>
          </Tooltip>
        ) : (
          content
        )}
      </ValueBox>
    </React.Fragment>
  );
}

export function ShowSettingOnOff({ controls, switchValue, switchId, ...other }) {
  return (
    <ShowSetting controls={controls} {...other}>
      <MuiSwitch
        checked={switchValue === 1}
        unsaved={controls.hasChanged(switchId)}
        onClick={(event) => controls.setValue(switchId, event.target.checked ? 1 : 0)}
        color="primary"
      />
    </ShowSetting>
  );
}

export function ShowSettingMinMax({
  cur_value_min,
  controller_value_min,
  cur_value_max,
  controller_value_max,
  ...other
}) {
  return (
    <ShowSetting
      cur_value={[cur_value_min, cur_value_max]}
      controller_value={[controller_value_min, controller_value_max]}
      {...other}
    />
  );
}

function ShowCurValue({
  text,
  name,
  unit,
  value,
  convert_to_min,
  convert_to_hhmmss,
  wrap_time_over_midnight,
  type,
  off_at,
  num_digits,
  ...other
}) {
  let { t } = useTranslation();

  // Update unit from defaults if not defined.
  unit = GetUnit(unit, name);

  // convert matching type
  if (type === 'float') {
    if (off_at !== undefined && value <= off_at) return <span>{t('off')}</span>;
    value = Number.parseFloat(value / 10 ** num_digits).toFixed(num_digits);
  } else if (type === 'negative') value = value - 127;
  else if (type === 'fan_min_max') {
    off_at = off_at === undefined ? 9 : off_at;
    if (value <= off_at) return <span>{t('off')}</span>;
  } else if (off_at !== undefined) {
    if (value <= off_at) return <span>{t('off')}</span>;
  }

  // Convert current value from minutes to a string format "HH:MM"
  if (convert_to_min) {
    unit = '';

    // Convert value to hours and minutes
    let hours = Math.floor(value / 60);
    const minutes = value % 60;
    if (hours >= 24) {
      hours = hours % 24;
    }
    value = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  }

  if (convert_to_hhmmss) {
    let hours = 0;
    let minutes = 0;
    let seconds = 0;
    if (unit.indexOf('sec') !== -1) {
      hours = Math.floor(value / 3600);
      minutes = Math.floor((value % 3600) / 60);
      seconds = value % 60;
    } else if (unit.indexOf('min') !== -1) {
      hours = Math.floor(value / 60);
      minutes = value % 60;
    }
    if (wrap_time_over_midnight && hours >= 24) {
      hours = hours % 24;
    }
    unit = '';
    value = `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  return (
    <span>
      {value}
      {unit}
    </span>
  );
}
